import { FilesService, type TCFile } from 'src/api/generated';

interface UploadFileMetadata {
  /** File name to declare when creating the upload session. */
  fileName: string;
  /** MIME type to declare when creating the upload session. */
  contentType: string;
}

interface FileChecksum {
  /** SHA256 checksum encoded as lowercase hex. */
  hex: string;
  /** SHA256 checksum encoded as base64 for S3 checksum headers. */
  base64: string;
}

interface FilesUploadSessionParams {
  /** Browser file to upload directly to Files storage. */
  file: File;
  /** Optional Files metadata tags. */
  tags?: Record<string, string>;
}

interface FileSignature {
  /** Canonical extension for the detected file content. */
  extension: string;
  /** Canonical MIME type for the detected file content. */
  contentType: string;
  /** MIME types accepted as equivalent declarations for this content. */
  acceptedContentTypes: readonly string[];
}

const HEADER_SNIFF_BYTES = 16;

const FILE_SIGNATURES = {
  pdf: {
    extension: 'pdf',
    contentType: 'application/pdf',
    acceptedContentTypes: ['application/pdf'],
  },
  png: {
    extension: 'png',
    contentType: 'image/png',
    acceptedContentTypes: ['image/png'],
  },
  jpg: {
    extension: 'jpg',
    contentType: 'image/jpeg',
    acceptedContentTypes: ['image/jpeg', 'image/jpg'],
  },
  gif: {
    extension: 'gif',
    contentType: 'image/gif',
    acceptedContentTypes: ['image/gif'],
  },
  webp: {
    extension: 'webp',
    contentType: 'image/webp',
    acceptedContentTypes: ['image/webp'],
  },
  mp4: {
    extension: 'mp4',
    contentType: 'video/mp4',
    acceptedContentTypes: ['video/mp4'],
  },
} satisfies Record<string, FileSignature>;

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
};

const bytesToHex = (bytes: Uint8Array): string =>
  Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

const calculateSHA256Checksum = async (file: File): Promise<FileChecksum> => {
  if (typeof globalThis.crypto === 'undefined') {
    throw new Error('Secure file checksums are not supported in this browser');
  }

  const digest = await globalThis.crypto.subtle.digest(
    'SHA-256',
    await file.arrayBuffer()
  );
  const bytes = new Uint8Array(digest);

  return {
    hex: bytesToHex(bytes),
    base64: bytesToBase64(bytes),
  };
};

const getFileExtension = (fileName: string): string => {
  const match = /\.([^.]+)$/u.exec(fileName);
  return match?.[1]?.toLowerCase() ?? '';
};

const withFileExtension = (fileName: string, extension: string): string => {
  const normalizedExtension = extension.replace(/^\./u, '');
  const lastSlashIndex = Math.max(
    fileName.lastIndexOf('/'),
    fileName.lastIndexOf('\\')
  );
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex > lastSlashIndex) {
    return `${fileName.slice(0, lastDotIndex)}.${normalizedExtension}`;
  }

  return `${fileName}.${normalizedExtension}`;
};

const startsWithBytes = (
  bytes: Uint8Array,
  expected: readonly number[],
  offset = 0
): boolean =>
  bytes.length >= offset + expected.length &&
  expected.every((byte, index) => bytes[offset + index] === byte);

const sniffFileSignature = async (
  file: File
): Promise<FileSignature | null> => {
  const header = new Uint8Array(
    await file.slice(0, HEADER_SNIFF_BYTES).arrayBuffer()
  );

  if (startsWithBytes(header, [0x25, 0x50, 0x44, 0x46])) {
    return FILE_SIGNATURES.pdf;
  }
  if (
    startsWithBytes(header, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
  ) {
    return FILE_SIGNATURES.png;
  }
  if (startsWithBytes(header, [0xff, 0xd8, 0xff])) {
    return FILE_SIGNATURES.jpg;
  }
  if (startsWithBytes(header, [0x47, 0x49, 0x46, 0x38])) {
    return FILE_SIGNATURES.gif;
  }
  if (
    startsWithBytes(header, [0x52, 0x49, 0x46, 0x46]) &&
    startsWithBytes(header, [0x57, 0x45, 0x42, 0x50], 8)
  ) {
    return FILE_SIGNATURES.webp;
  }
  if (startsWithBytes(header, [0x66, 0x74, 0x79, 0x70], 4)) {
    return FILE_SIGNATURES.mp4;
  }

  return null;
};

const resolveUploadFileMetadata = async (
  file: File
): Promise<UploadFileMetadata> => {
  const declaredContentType = file.type || 'application/octet-stream';
  const signature = await sniffFileSignature(file);

  if (!signature) {
    return {
      fileName: file.name,
      contentType: declaredContentType,
    };
  }

  const declaredExtension = getFileExtension(file.name);
  const contentTypeMatches = signature.acceptedContentTypes.includes(
    declaredContentType.toLowerCase()
  );
  const extensionMatches =
    declaredExtension === signature.extension ||
    (signature.extension === 'jpg' && declaredExtension === 'jpeg');

  if (contentTypeMatches && extensionMatches) {
    return {
      fileName: file.name,
      contentType: declaredContentType,
    };
  }

  return {
    fileName: withFileExtension(file.name, signature.extension),
    contentType: signature.contentType,
  };
};

const uploadFileToPresignedUrl = async (
  uploadUrl: string,
  uploadHeaders: Record<string, string>,
  file: File
): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: uploadHeaders,
    body: file,
  });

  if (!response.ok) {
    throw new Error(`File upload failed with status ${response.status}`);
  }
};

export const uploadFileWithUploadSession = async ({
  file,
  tags,
}: FilesUploadSessionParams): Promise<TCFile> => {
  if (file.size === 0) {
    throw new Error('File is empty');
  }

  const metadata = await resolveUploadFileMetadata(file);
  const checksum = await calculateSHA256Checksum(file);
  const uploadSession = await FilesService.createUploadSession({
    fileName: metadata.fileName,
    contentType: metadata.contentType,
    size: file.size,
    checksumSHA256Hex: checksum.hex,
    checksumSHA256Base64: checksum.base64,
    tags: tags ?? {},
  });

  if (!uploadSession.uploadUrl || !uploadSession.uploadSessionId) {
    throw new Error('File upload session response is incomplete');
  }

  await uploadFileToPresignedUrl(
    uploadSession.uploadUrl,
    uploadSession.uploadHeaders,
    file
  );

  const finalizedFile = await FilesService.finalizeUploadSession(
    uploadSession.uploadSessionId
  );

  if (!finalizedFile.id) {
    throw new Error('Finalized file response is incomplete');
  }

  return finalizedFile;
};
